import { Controller } from "@hotwired/stimulus"
import { deal, lengthMs, preview } from "../work/cues"
import { repaintSatchel } from "../lair/satchel"

// A shift of work (see Jobs). The server deals the shift — a job, a seed —
// and it is played here: the cues are dealt from the seed exactly as the
// server deals them, drawn in the yard as a float that dips, a rock face
// that cracks to a beat, or herbs that show in the undergrowth, and the
// hand presses. Each press is kept with its moment (and, for the herbs,
// its place); the flash shown is a preview, and when the last cue is past
// the presses go to the server, whose verdict pays.
//
// Three drawings, one clock. The yard is a unit square: a cue's x and y are
// fractions across and down it, so the same numbers draw at any size.
const RING_MS = 900
const SHOW_AFTER_MS = 700
const READY_MS = 1100
const FLASH_MS = 520
const WORDS = { sharp: "Sharp", hit: "Hit", miss: "Miss" }

export default class extends Controller {
  static targets = [ "stage", "yard", "scene", "cues", "flash", "dragon", "call", "marks", "error", "jobs", "left" ]
  static values = { startUrl: String, finishUrl: String, fullLeft: Number }

  connect() {
    this.shift = null
    this.onPointerDown = this.pointerDown.bind(this)
    this.onKeyDown = this.keyDown.bind(this)
    this.yardTarget.addEventListener("pointerdown", this.onPointerDown)
    this.yardTarget.addEventListener("keydown", this.onKeyDown)
    this.loop = this.frame.bind(this)
  }

  disconnect() {
    cancelAnimationFrame(this.frameId)
    clearTimeout(this.flashTimer)
    this.yardTarget.removeEventListener("pointerdown", this.onPointerDown)
    this.yardTarget.removeEventListener("keydown", this.onKeyDown)
  }

  // ------------------------------------------------------------------ start

  async start(event) {
    if (this.shift || this.busy) return
    const job = event.params.job
    this.busy = true
    this.showError(null)
    this.buttons().forEach((button) => { button.disabled = true })

    try {
      const response = await fetch(this.startUrlValue.replace("JOB", job), { method: "POST", headers: this.headers() })
      const payload = await response.json()
      if (!response.ok) throw new Error(payload.error || "The foreman shook his head.")
      this.begin(payload)
    } catch (error) {
      this.showError(error.message)
      this.buttons().forEach((button) => { button.disabled = false })
    } finally {
      this.busy = false
    }
  }

  begin(payload) {
    this.shift = payload
    this.job = payload.job
    this.cues = deal(payload.seed, this.job)
    this.length = lengthMs(this.cues, this.job)
    this.presses = []
    this.spent = new Set()
    this.shown = new Set()
    this.marks = this.cues.map(() => null)
    this.stageTarget.hidden = false
    this.yardTarget.dataset.game = this.job.game
    this.yardTarget.classList.add("ll-shift__yard--live")
    this.drawScene()
    this.drawCues()
    this.drawMarks()
    this.say(`${payload.place.replace(/^the /, "The ")}. ${this.readyLine()}`)
    this.yardTarget.focus({ preventScroll: true })
    this.t0 = performance.now() + READY_MS
    this.frameId = requestAnimationFrame(this.loop)
  }

  readyLine() {
    switch (this.job.game) {
      case "bite": return "Press as the ring closes on the float."
      case "strike": return "Three strikes to the beat when it cracks."
      default: return "Tap each herb as it appears."
    }
  }

  // ------------------------------------------------------------------ frame

  frame(now) {
    if (!this.shift) return
    const ms = now - this.t0
    this.cues.forEach((cue, index) => this.drawCue(cue, index, ms))
    if (ms > this.length) return this.finish()
    this.frameId = requestAnimationFrame(this.loop)
  }

  drawCue(cue, index, ms) {
    const node = this.cueNodes[index]
    const dt = ms - cue.at
    if (this.job.game === "spot") {
      const showing = dt >= 0 && dt <= this.job.windowMs && this.marks[index] === null
      node.hidden = !showing && this.marks[index] === null
      node.classList.toggle("ll-shift__cue--showing", showing)
      if (dt > this.job.windowMs && this.marks[index] === null) this.mark(index, "miss")
      return
    }

    // The ring closes on the float (or the crack) over RING_MS and is gone a
    // window after; a cue nobody pressed is a miss once its window shuts.
    const visible = dt >= -RING_MS && dt <= this.job.windowMs + 200
    node.hidden = !visible && this.marks[index] === null
    if (visible) {
      const closing = Math.max(0, Math.min(1, 1 - (-dt / RING_MS)))
      node.style.setProperty("--ring", (1.9 - closing * 0.9).toFixed(3))
      node.classList.toggle("ll-shift__cue--near", Math.abs(dt) <= this.job.windowMs)
      node.classList.toggle("ll-shift__cue--dipped", dt >= 0 && dt <= 260)
    }
    if (dt > this.job.windowMs && this.marks[index] === null) this.mark(index, "miss")
  }

  // ------------------------------------------------------------------ press

  pointerDown(event) {
    if (!this.shift) return
    event.preventDefault()
    const rect = this.yardTarget.getBoundingClientRect()
    const x = (event.clientX - rect.left) / rect.width
    const y = (event.clientY - rect.top) / rect.height
    this.press({ x: Math.min(1, Math.max(0, x)), y: Math.min(1, Math.max(0, y)) })
  }

  keyDown(event) {
    if (!this.shift || this.job.game === "spot") return
    if (event.code !== "Space" && event.code !== "Enter") return
    event.preventDefault()
    this.press({ x: 0.5, y: 0.5 })
  }

  press(where) {
    const ms = performance.now() - this.t0
    if (ms < 0) return
    const press = { ms: Math.round(ms), x: Number(where.x.toFixed(4)), y: Number(where.y.toFixed(4)) }
    this.presses.push(press)
    const verdict = preview(this.cues, press, this.job, this.spent)
    if (verdict.index !== null) {
      this.spent.add(verdict.index)
      this.mark(verdict.index, verdict.mark)
    }
    this.flash(verdict.mark, where)
  }

  mark(index, mark) {
    if (this.marks[index] !== null) return
    this.marks[index] = mark
    const node = this.cueNodes[index]
    node.classList.remove("ll-shift__cue--near", "ll-shift__cue--showing")
    node.classList.add(`ll-shift__cue--${mark}`)
    if (mark !== "miss") this.dragonCheers()
    this.drawMarks()
  }

  flash(mark, where) {
    const node = this.flashTarget
    node.textContent = WORDS[mark]
    node.dataset.mark = mark
    node.style.left = `${(where.x * 100).toFixed(1)}%`
    node.style.top = `${(where.y * 100).toFixed(1)}%`
    node.classList.remove("ll-shift__flash--in")
    void node.offsetWidth
    node.classList.add("ll-shift__flash--in")
    clearTimeout(this.flashTimer)
    this.flashTimer = setTimeout(() => node.classList.remove("ll-shift__flash--in"), FLASH_MS)
  }

  dragonCheers() {
    if (!this.hasDragonTarget) return
    this.dragonTarget.classList.remove("ll-shift__dragon--glad")
    void this.dragonTarget.offsetWidth
    this.dragonTarget.classList.add("ll-shift__dragon--glad")
  }

  // ----------------------------------------------------------------- finish

  async finish() {
    const shift = this.shift
    this.shift = null
    this.yardTarget.classList.remove("ll-shift__yard--live")
    this.say("The foreman weighs it…")

    try {
      const response = await fetch(this.finishUrlValue.replace("SHIFT", shift.id), {
        method: "POST", headers: this.headers(), body: JSON.stringify({ presses: this.presses })
      })
      const payload = await response.json()
      if (!response.ok) throw new Error(payload.error || "The foreman shook his head.")
      this.settle(payload)
    } catch (error) {
      this.showError(error.message)
    } finally {
      this.buttons().forEach((button) => { button.disabled = false })
    }
  }

  settle(payload) {
    const trickle = payload.full ? "" : " A trickle, the day's three being worked."
    const found = payload.materials_line ? ` ${payload.materials_line}` : ""
    this.say(`${payload.line} ${payload.points} of ${payload.max_points}: ${payload.silver} silver.${trickle}${found}`)
    if (payload.satchel) repaintSatchel(payload.satchel)
    payload.marks.forEach((mark, index) => { this.marks[index] = mark })
    this.drawMarks()
    this.fullLeftValue = payload.full_left
    if (this.hasLeftTarget) {
      this.leftTarget.textContent = payload.full_left > 0
        ? `${payload.full_left} full ${payload.full_left === 1 ? "shift" : "shifts"} left today`
        : "the day's three are worked; the rest pay a trickle"
    }
    if (payload.purse) {
      // The masthead's chip first, so it sees the change and ticks
      // (purse_controller); then every other count on the page.
      document.dispatchEvent(new CustomEvent("purse:changed", { detail: payload.purse }))
      document.querySelectorAll(".ll-silver-count").forEach((node) => { node.textContent = payload.purse.silver })
      document.querySelectorAll(".ll-gold-count").forEach((node) => { node.textContent = payload.purse.gold })
    }
    this.element.dispatchEvent(new CustomEvent("shift:paid", { bubbles: true, detail: payload }))
  }

  // ------------------------------------------------------------------ drawing

  drawScene() {
    const scene = this.sceneTarget
    scene.innerHTML = ""
    scene.dataset.game = this.job.game
  }

  drawCues() {
    const holder = this.cuesTarget
    holder.innerHTML = ""
    this.cueNodes = this.cues.map((cue, index) => {
      const node = document.createElement("div")
      node.className = "ll-shift__cue"
      node.dataset.index = index
      node.style.left = `${(cue.x * 100).toFixed(2)}%`
      node.style.top = `${(cue.y * 100).toFixed(2)}%`
      node.hidden = true
      node.innerHTML = `<span class="ll-shift__ring"></span><span class="ll-shift__mark"></span>`
      holder.appendChild(node)
      return node
    })
  }

  drawMarks() {
    const list = this.marksTarget
    list.innerHTML = ""
    this.marks.forEach((mark) => {
      const item = document.createElement("li")
      item.className = `ll-shift__tick ${mark ? `ll-shift__tick--${mark}` : ""}`
      item.textContent = mark ? WORDS[mark] : "·"
      list.appendChild(item)
    })
  }

  // ------------------------------------------------------------------- misc

  say(text) {
    if (this.hasCallTarget) this.callTarget.textContent = text
  }

  showError(message) {
    this.errorTarget.hidden = !message
    this.errorTarget.textContent = message || ""
  }

  buttons() {
    return Array.from(this.jobsTarget.querySelectorAll("button[data-action]"))
  }

  headers() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
    }
  }
}
